import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { DEFAULT_SETTINGS, STORAGE_KEYS } from '../constants';
import { useLocation } from 'react-router-dom';

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(DEFAULT_SETTINGS.VOICE_VOLUME);
  const [isBackgroundMusicPlaying, setIsBackgroundMusicPlaying] = useState(false);
  const [backgroundMusicVolume, setBackgroundMusicVolume] = useState(DEFAULT_SETTINGS.BACKGROUND_MUSIC_VOLUME);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [voicePreference, setVoicePreference] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.VOICE_PREFERENCE) || 'male';
  });
  const [playbackRate, setPlaybackRate] = useState(() => {
    const savedRate = localStorage.getItem(STORAGE_KEYS.PLAYBACK_RATE);
    return savedRate ? parseFloat(savedRate) : DEFAULT_SETTINGS.PLAYBACK_RATE;
  });
  
  const audioRef = useRef(null);
  const backgroundMusicRef = useRef(null);

  const location = useLocation();
  const showAudio = location.pathname.startsWith('/story') || isPlaying;

  const handlePlayPause = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error('Audio playback error:', error);
            setIsPlaying(false);
          });
      }
    }
  }, [isPlaying]);

  const handleStop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  }, []);

  const handleVolumeChange = useCallback((newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  const handleBackgroundMusicVolumeChange = useCallback((newVolume) => {
    setBackgroundMusicVolume(newVolume);
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = newVolume;
    }
  }, []);

  const handlePlaybackRateChange = useCallback((newRate) => {
    setPlaybackRate(newRate);
    // Save to localStorage
    localStorage.setItem(STORAGE_KEYS.PLAYBACK_RATE, newRate.toString());
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  }, []);

  const toggleBackgroundMusic = useCallback(() => {
    if (backgroundMusicRef.current) {
      if (isBackgroundMusicPlaying) {
        backgroundMusicRef.current.pause();
      } else {
        backgroundMusicRef.current.play().catch(console.error);
      }
      setIsBackgroundMusicPlaying(!isBackgroundMusicPlaying);
    }
  }, [isBackgroundMusicPlaying]);

  const handleCanPlayThrough = useCallback(() => {
    setIsAudioLoaded(true);
  }, []);

  // Handle voice preference changes
  const handleVoicePreferenceChange = useCallback((newVoice) => {
    setVoicePreference(newVoice);
    localStorage.setItem(STORAGE_KEYS.VOICE_PREFERENCE, newVoice);
  }, []);

  // Keep volume state in sync with audio elements
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (backgroundMusicRef.current) {
      backgroundMusicRef.current.volume = backgroundMusicVolume;
    }
  }, [backgroundMusicVolume]);

  useEffect(() => {
    const currentAudioRef = audioRef.current;
    
    if (currentAudioRef) {
      currentAudioRef.addEventListener('canplaythrough', handleCanPlayThrough);
    }
    
    return () => {
      if (currentAudioRef) {
        currentAudioRef.removeEventListener('canplaythrough', handleCanPlayThrough);
      }
    };
  }, [handleCanPlayThrough]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  useEffect(() => {
    if (!location.pathname.startsWith('/story')) {
      if (audioRef.current) {
        audioRef.current.pause();
        setCurrentTime(0);
      }
    }
  }, [location.pathname]);

  return (
    <AudioContext.Provider value={{
      isPlaying,
      currentTime,
      duration,
      volume,
      voicePreference,
      isBackgroundMusicPlaying,
      backgroundMusicVolume,
      isAudioLoaded,
      playbackRate,
      audioRef,
      backgroundMusicRef,
      setIsPlaying,
      setVoicePreference: handleVoicePreferenceChange,
      setVolume: handleVolumeChange,
      setBackgroundMusicVolume: handleBackgroundMusicVolumeChange,
      setPlaybackRate: handlePlaybackRateChange,
      setIsAudioLoaded,
      handlePlayPause,
      handleStop,
      handleTimeUpdate,
      toggleBackgroundMusic,
      setIsBackgroundMusicPlaying,
      handleCanPlayThrough,
      showAudio
    }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext);